import React, { useEffect, useState } from 'react';
import { Form, Select, Button, Input, Spin } from 'antd';
import { GlobalOutlined, LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import api from '../services/api';
import { authService } from '../services/auth';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';
import { ObjectCardSection } from '../components/layout/ObjectCard';
import '../components/layout/ObjectCardExtras.css';

interface UserSettings {
  language: string;
  currency: string;
}

const Settings: React.FC = () => {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const feedback = useFeedback();
  const { changeLanguage } = useLanguage();
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const user = authService.getCurrentUser();

  const currencies = [
    { value: 'USD', label: t('settings:currencies.USD') },
    { value: 'EUR', label: t('settings:currencies.EUR') },
    { value: 'PLN', label: t('settings:currencies.PLN') },
  ];

  const languages = [
    { value: 'en', label: t('settings:languages.en') },
    { value: 'pl', label: t('settings:languages.pl') },
  ];

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      form.setFieldsValue({
        language: response.data.user?.language || i18n.language,
        currency: response.data.user?.preferredCurrency || 'EUR',
      });
    } catch (error) {
      // If settings don't exist yet, use defaults
      form.setFieldsValue({
        language: i18n.language,
        currency: 'EUR',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (values: UserSettings) => {
    setSaving(true);
    try {
      await api.put('/settings', {
        language: values.language,
        preferredCurrency: values.currency,
      });
      
      // Update the language immediately if it changed
      if (values.language !== i18n.language) {
        await changeLanguage(values.language);
      }

      feedback.success(t('settings:messages.saveSuccess'));
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'settings:messages.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (values: { currentPassword: string; newPassword: string; confirmPassword: string }) => {
    if (values.newPassword !== values.confirmPassword) {
      feedback.error(t('settings:messages.passwordMismatch'));
      return;
    }
    setChangingPassword(true);
    try {
      await api.put('/users/me/password', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      feedback.success(t('settings:messages.passwordChanged'));
      passwordForm.resetFields();
    } catch (error: any) {
      if (error.response?.data?.error === 'INVALID_PASSWORD') {
        feedback.error(t('settings:messages.currentPasswordWrong'));
      } else {
        feedback.error(getApiErrorMessage(error, t, 'settings:messages.passwordError'));
      }
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return <div className="obj-loading"><Spin size="large" /></div>;
  }

  // Apple-style settings (DESIGN.md „Direction since 2026-10-19”): two equal
  // sections, each a grouped list with the label left and the field right.
  // The save button sits in the section header; a hidden submit keeps Enter working.
  return (
    <div className="obj-card settings-page">
      <div className="obj-card__body">
        <ObjectCardSection
          title={t('settings:userPreferences')}
          icon={<GlobalOutlined />}
          tone="blue"
          hint={t('settings:userPreferencesHint')}
          actions={(
            <Button type="primary" size="small" loading={saving} onClick={() => form.submit()}>
              {t('common:save')}
            </Button>
          )}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSaveSettings}
            onFinishFailed={() => feedback.warning(t('settings:messages.fillRequired'))}
            scrollToFirstError
          >
            <div className="obj-form">
              {user && (
                <Form.Item label={t('settings:currentUser')}>
                  <span className="tool-form__value" title={`${user.name} · ${t(`common:userRole.${user.role}`)}`}>
                    {user.name} · {t(`common:userRole.${user.role}`)}
                  </span>
                </Form.Item>
              )}
              <Form.Item
                name="language"
                label={t('settings:language')}
                rules={[{ required: true, message: t('settings:validation.languageRequired') }]}
              >
                <Select options={languages} />
              </Form.Item>

              <Form.Item
                name="currency"
                label={t('settings:currency')}
                rules={[{ required: true, message: t('settings:validation.currencyRequired') }]}
              >
                <Select options={currencies} />
              </Form.Item>
            </div>
            <button type="submit" hidden tabIndex={-1} />
          </Form>
        </ObjectCardSection>

        <ObjectCardSection
          title={t('settings:changePassword')}
          icon={<LockOutlined />}
          tone="gray"
          hint={t('settings:changePasswordHint')}
          actions={(
            <Button type="primary" size="small" loading={changingPassword} onClick={() => passwordForm.submit()}>
              {t('settings:changePassword')}
            </Button>
          )}
        >
          <Form
            form={passwordForm}
            layout="vertical"
            onFinish={handleChangePassword}
            onFinishFailed={() => feedback.warning(t('settings:messages.fillRequired'))}
            scrollToFirstError
          >
            <div className="obj-form">
              <Form.Item
                name="currentPassword"
                label={t('settings:currentPassword')}
                rules={[{ required: true, message: t('settings:validation.currentPasswordRequired') }]}
              >
                <Input.Password autoComplete="current-password" />
              </Form.Item>

              <Form.Item
                name="newPassword"
                label={t('settings:newPassword')}
                rules={[
                  { required: true, message: t('settings:validation.newPasswordRequired') },
                  { min: 6, message: t('settings:validation.newPasswordMin') },
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label={t('settings:confirmPassword')}
                rules={[
                  { required: true, message: t('settings:validation.confirmPasswordRequired') },
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>
            </div>
            <button type="submit" hidden tabIndex={-1} />
          </Form>
        </ObjectCardSection>
      </div>
    </div>
  );
};

export default Settings;